import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldWrapper } from '@ngx-formly/core';

import { TippyTooltipDirective } from '../../directives/tippy-tooltip.directive';

/** Generic outer Formly wrapper for semantic hover-help configured through `props.hoverTooltip`. */
@Component({
  selector: 'formly-wrapper-hover-tooltip',
  template: `
    <div
      #tooltipAnchor
      class="dd-hover-tooltip-anchor"
      [attr.tabindex]="anchorIsKeyboardTarget() && props.hoverTooltip ? 0 : null"
      [tippyTooltip]="props.hoverTooltip"
      [tippyTooltipTriggerTarget]="tooltipTriggerTargets()"
    >
      <ng-container #fieldComponent></ng-container>
    </div>
  `,
  imports: [TippyTooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: contents;
    }

    .dd-hover-tooltip-anchor {
      display: block;
      width: 100%;
    }
  `,
})
export class HoverTooltipWrapper extends FieldWrapper implements AfterViewInit {
  readonly tooltipAnchor = viewChild.required<ElementRef<HTMLElement>>('tooltipAnchor');
  readonly tooltipTriggerTargets = signal<HTMLElement | HTMLElement[] | null>(null);
  readonly anchorIsKeyboardTarget = signal(false);

  readonly #destroyRef = inject(DestroyRef);
  #controlsObserver: MutationObserver | null = null;

  ngAfterViewInit(): void {
    this.#syncTriggerTargets();
    this.formControl?.statusChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.#syncTriggerTargets());

    this.#controlsObserver = new MutationObserver(() => this.#syncTriggerTargets());
    this.#controlsObserver.observe(this.tooltipAnchor().nativeElement, { childList: true, subtree: true });
    this.#destroyRef.onDestroy(() => this.#controlsObserver?.disconnect());
  }

  #syncTriggerTargets(): void {
    const anchor = this.tooltipAnchor().nativeElement;
    const controls = Array.from(
      anchor.querySelectorAll<HTMLElement>(
        'a[href], button, input:not([type="hidden"]), select, textarea, [contenteditable="true"], [tabindex]'
      )
    ).filter(
      element =>
        element !== anchor &&
        !element.matches(':disabled, [aria-disabled="true"], [tabindex="-1"], .mat-mdc-tooltip-trigger') &&
        !element.closest('app-info-tooltip')
    );

    const useAnchor = !!this.props.disabled || !!this.formControl?.disabled || controls.length === 0;
    this.anchorIsKeyboardTarget.set(useAnchor);
    this.tooltipTriggerTargets.set(useAnchor ? anchor : controls);
  }
}
